import { useState, useEffect } from 'react';

/**
 * PWA Install prompt.
 * Uses sessionStorage so the banner re-appears every new app open (session)
 * until the user actually installs it. Once installed, never shows again (localStorage).
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled]       = useState(false);
  const [showIOSBanner, setShowIOSBanner]   = useState(false);
  // dismissed = only for this session (sessionStorage resets on next app open)
  const [dismissed, setDismissed]           = useState(
    () => sessionStorage.getItem('pwa_prompt_dismissed_session') === 'true'
  );

  useEffect(() => {
    // Already installed as PWA — never show
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    ) {
      setIsInstalled(true);
      return;
    }

    // Android / Chrome
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari detection
    const isIOS    = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent);
    if (isIOS && isSafari && !dismissed) {
      setShowIOSBanner(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [dismissed]);

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      // Permanently mark as installed
      localStorage.setItem('pwa_installed', 'true');
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    // Dismiss only for THIS session — will reappear next time app is opened
    setDismissed(true);
    setShowIOSBanner(false);
    setDeferredPrompt(null);
    sessionStorage.setItem('pwa_prompt_dismissed_session', 'true');
  };

  return {
    canInstall:    !!deferredPrompt && !dismissed && !isInstalled,
    showIOSBanner: showIOSBanner && !isInstalled,
    isInstalled,
    triggerInstall,
    dismiss,
  };
}
